import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    AlertTriangle, RefreshCcw, ShieldCheck, ShieldAlert, Truck, FileText,
    BadgeCheck, Building2, Activity, ChevronDown, ChevronRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatPhoneForDisplay } from "@/lib/phone";
import { toast } from "sonner";
import { formatEin } from "@/lib/ein";

/**
 * Phase QCM — Admin "FMCSA Snapshot" panel.
 *
 * Renders a normalized FMCSA snapshot (QCMobile JSON + SAFER mailing/phone)
 * for a given USDOT number. Sections: Identity, Authority, Safety, Crashes
 * (24mo), OOS rates (24mo), Insurance, Operations. Includes "Refresh from
 * FMCSA" button that force-refreshes the 24h cache.
 *
 * Empty state: no DOT on file → friendly hint.
 * Error state: 4xx/5xx → inline retry button + error reason.
 */

const A_N_I_TONE = {
    A: "bg-emerald-50 text-emerald-800 border-emerald-200",
    Y: "bg-emerald-50 text-emerald-800 border-emerald-200",
    N: "bg-slate-100 text-slate-700 border-slate-200",
    I: "bg-amber-50 text-amber-900 border-amber-200",
    u: "bg-slate-100 text-slate-500 border-slate-200",
};

const SAFETY_RATING_LABEL = { S: "Satisfactory", C: "Conditional", U: "Unsatisfactory" };
const SAFETY_RATING_TONE = {
    S: "bg-emerald-50 text-emerald-800 border-emerald-200",
    C: "bg-amber-50 text-amber-900 border-amber-200",
    U: "bg-red-50 text-red-800 border-red-200",
};

function Chip({ tone = "slate", children, testId }) {
    const cls = A_N_I_TONE[tone] || tone || A_N_I_TONE.u;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
            data-testid={testId}
        >
            {children}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
    catch { return iso; }
}

function fmtNum(n) {
    if (n === null || n === undefined) return "—";
    return new Intl.NumberFormat().format(n);
}

function Row({ label, children, testId }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1.5">
            <dt className="text-xs uppercase tracking-wider text-slate-500 shrink-0">{label}</dt>
            <dd className="text-sm text-slate-900 text-right" data-testid={testId}>{children}</dd>
        </div>
    );
}

function Section({ title, icon: Icon, children, defaultOpen = true, testId }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border-b border-slate-100 last:border-0 py-3" data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center justify-between w-full text-left"
            >
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    {Icon && <Icon size={14} className="text-blue-900" />}
                    {title}
                </span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <div className="mt-2">{children}</div>}
        </div>
    );
}

function OosRateRow({ label, group, testIdBase }) {
    if (!group) return null;
    const rate = group.oos_rate;
    const nat = group.national_avg;
    let tone = "text-slate-700";
    if (rate != null && nat != null && nat > 0 && rate > nat * 1.5) {
        tone = "text-red-700 font-semibold";
    }
    return (
        <div className="flex items-center justify-between text-xs py-1" data-testid={`${testIdBase}-row`}>
            <span className="text-slate-600">{label}</span>
            <span className="tabular-nums">
                <span className={tone} data-testid={`${testIdBase}-rate`}>
                    {rate != null ? rate.toFixed(2) : "—"}%
                </span>
                <span className="text-slate-400 ml-2">vs nat. {nat != null ? nat.toFixed(2) : "—"}%</span>
            </span>
        </div>
    );
}

function InsuranceRow({ label, group, testIdBase }) {
    if (!group) return null;
    const onFile = group.on_file ?? 0;
    const req = group.required;
    const reqK = group.required_amount_thousands;
    let body;
    if (req === "u") body = <span className="text-slate-500 text-xs">Not on file</span>;
    else if (req === "Y" || req === "N") {
        const reqLabel = reqK ? `$${fmtNum(reqK * 1000)}` : (req === "Y" ? "Required" : "Not required");
        const tone = req === "Y" && onFile === 0 ? "text-red-700 font-semibold" : "text-slate-800";
        body = (
            <span className="text-xs tabular-nums">
                <span className={tone} data-testid={`${testIdBase}-onfile`}>On file: {fmtNum(onFile)}</span>
                <span className="text-slate-400 ml-2">/ Req: {reqLabel}</span>
            </span>
        );
    } else {
        body = <span className="text-slate-500 text-xs">—</span>;
    }
    return (
        <div className="flex items-center justify-between py-1" data-testid={`${testIdBase}-row`}>
            <span className="text-xs text-slate-600">{label}</span>
            {body}
        </div>
    );
}

export function FmcsaSnapshotPanel({
    dotNumber,
    onApplyEin,
    // Phase QCM-EXT followup — optional outer collapse mechanic.
    // When `collapsible=true`, the existing CardHeader (which holds the
    // Refresh button + retrieval-date subline) becomes click-to-toggle
    // and the CardContent below is hidden when collapsed. The Refresh
    // button stays visible in the header even when collapsed.
    //
    // Default state when no localStorage entry yet: collapsed=true
    // (admins explicitly asked for default-collapsed). Persist the
    // user's choice via `storageKey` in localStorage.
    collapsible = false,
    storageKey = "",
    defaultCollapsed = true,
}) {
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [refreshing, setRefreshing] = useState(false);

    const [collapsed, setCollapsed] = useState(() => {
        if (!collapsible || typeof window === "undefined") return defaultCollapsed;
        try {
            const v = window.localStorage.getItem(storageKey);
            if (v === null) return defaultCollapsed;
            return v === "1";
        } catch (e) { return defaultCollapsed; }
    });

    useEffect(() => {
        if (!collapsible || !storageKey || typeof window === "undefined") return;
        try { window.localStorage.setItem(storageKey, collapsed ? "1" : "0"); } catch (e) { /* best-effort */ }
    }, [collapsed, collapsible, storageKey]);

    const load = useCallback(async (force = false) => {
        if (!dotNumber) return;
        if (force) setRefreshing(true);
        else setLoading(true);
        setError("");
        try {
            const url = force
                ? `/admin/fmcsa/snapshot/${dotNumber}?force_refresh=true`
                : `/admin/fmcsa/snapshot/${dotNumber}`;
            const { data } = await api.get(url);
            setSnap(data);
            if (force) toast.success("FMCSA snapshot refreshed.");
        } catch (e) {
            setError(formatApiError(e));
            setSnap(null);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [dotNumber]);

    useEffect(() => { load(false); }, [load]);

    if (!dotNumber) {
        return (
            <Card data-testid="fmcsa-snapshot-empty">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Truck size={16} className="text-blue-900" /> FMCSA Snapshot
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-slate-500">No DOT on file — add a USDOT number to enable the FMCSA snapshot.</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="fmcsa-snapshot-panel" data-collapsed={collapsed ? "true" : "false"}>
            <CardHeader
                className={`flex flex-row items-start justify-between gap-3 space-y-0 ${collapsible ? "cursor-pointer hover:bg-slate-50 rounded-t-md" : ""}`}
                onClick={collapsible ? () => setCollapsed((v) => !v) : undefined}
                role={collapsible ? "button" : undefined}
                tabIndex={collapsible ? 0 : undefined}
                onKeyDown={collapsible ? (e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setCollapsed((v) => !v); }
                } : undefined}
                aria-expanded={collapsible ? !collapsed : undefined}
                data-testid={collapsible ? "fmcsa-snapshot-toggle" : undefined}
            >
                <div className="flex items-center gap-2 min-w-0">
                    {collapsible && (
                        collapsed
                            ? <ChevronRight size={16} className="text-slate-500 shrink-0" />
                            : <ChevronDown size={16} className="text-slate-500 shrink-0" />
                    )}
                    <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Truck size={16} className="text-blue-900" /> FMCSA Snapshot
                        </CardTitle>
                        {snap?.retrieval_date_utc && (
                            <p className="text-[11px] text-slate-500 mt-1" data-testid="fmcsa-snapshot-retrieved-at">
                                Sourced {fmtDate(snap.retrieval_date_utc)}{snap.cache_hit ? " (cached ≤24h)" : ""}
                            </p>
                        )}
                    </div>
                </div>
                {/* Refresh stays visible in the header even when the
                    section is collapsed; stopPropagation so the click
                    doesn't toggle the section. */}
                <Button
                    variant="outline" size="sm"
                    onClick={(e) => { e.stopPropagation(); load(true); }}
                    disabled={refreshing || loading}
                    className="gap-1.5 shrink-0"
                    data-testid="fmcsa-snapshot-refresh"
                >
                    <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
                    {refreshing ? "Refreshing…" : "Refresh from FMCSA"}
                </Button>
            </CardHeader>
            {(!collapsible || !collapsed) && (
                <CardContent>
                    {loading && <p className="text-sm text-slate-500" data-testid="fmcsa-snapshot-loading">Loading FMCSA snapshot…</p>}
                {error && !loading && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="fmcsa-snapshot-error">
                        {error}
                    </div>
                )}
                {snap && !loading && (
                    <div>
                        {snap.allowed_to_operate === "N" && (
                            <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="fmcsa-snapshot-not-authorized-warning">
                                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                FMCSA shows this carrier is currently <strong>not authorized to operate</strong>.
                            </div>
                        )}

                        <Section title="Identity" icon={Building2} testId="fmcsa-snapshot-section-identity">
                            <dl className="divide-y divide-slate-50">
                                <Row label="Legal name" testId="fmcsa-snap-legal-name">{snap.legal_name || "—"}</Row>
                                <Row label="DBA">{snap.dba_name || "—"}</Row>
                                <Row label="USDOT">{snap.dot_number}</Row>
                                <Row label="MC #" testId="fmcsa-snap-mc-numbers">
                                    {snap.mc_numbers && snap.mc_numbers.length ? snap.mc_numbers.join(", ") : "—"}
                                </Row>
                                <Row label="EIN" testId="fmcsa-snap-ein">
                                    <span className="inline-flex items-center gap-2">
                                        {/* Phase QCM-EIN — render the
                                            canonical XX-XXXXXXX form
                                            everywhere; "Apply to customer"
                                            writes the formatted string. */}
                                        {snap.ein ? <span className="font-mono">{formatEin(snap.ein)}</span> : <span className="text-slate-400">—</span>}
                                        {snap.ein && onApplyEin && (
                                            <Button
                                                size="sm" variant="ghost"
                                                onClick={() => onApplyEin(formatEin(snap.ein))}
                                                className="h-6 px-2 text-[11px] text-blue-900"
                                                data-testid="fmcsa-snap-apply-ein"
                                            >Apply to customer</Button>
                                        )}
                                    </span>
                                </Row>
                                <Row label="Entity type">{snap.entity_type || "—"}</Row>
                                <Row label="Passenger?">{snap.is_passenger_carrier === "Y" ? "Yes" : "No"}</Row>
                                <Row label="MCS-150 outdated">
                                    {snap.mcs150_outdated === "Y"
                                        ? <Chip tone="I">Outdated</Chip>
                                        : <Chip tone="Y">Current</Chip>}
                                </Row>
                                <Row label="Physical address">
                                    {[snap.physical_address?.street, snap.physical_address?.city, snap.physical_address?.state, snap.physical_address?.zip].filter(Boolean).join(", ") || "—"}
                                </Row>
                                <Row label="Mailing address">{snap.mailing_address || "—"}</Row>
                                <Row label="Phone">{formatPhoneForDisplay(snap.phone) || snap.phone || "—"}</Row>
                            </dl>
                        </Section>

                        <Section title="Authority" icon={BadgeCheck} testId="fmcsa-snapshot-section-authority">
                            <div className="flex flex-wrap gap-2 mb-2">
                                <Chip tone={snap.authority?.common} testId="fmcsa-snap-authority-common">Common: {snap.authority?.common || "—"}</Chip>
                                <Chip tone={snap.authority?.contract} testId="fmcsa-snap-authority-contract">Contract: {snap.authority?.contract || "—"}</Chip>
                                <Chip tone={snap.authority?.broker} testId="fmcsa-snap-authority-broker">Broker: {snap.authority?.broker || "—"}</Chip>
                                <Chip tone={snap.allowed_to_operate} testId="fmcsa-snap-allowed-to-operate">Allowed to operate: {snap.allowed_to_operate || "—"}</Chip>
                            </div>
                            <p className="text-[11px] text-slate-500">A = Active · N = Not authorized · I = Inactive · u = N/A</p>
                        </Section>

                        <Section title="Safety" icon={ShieldCheck} testId="fmcsa-snapshot-section-safety">
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                                <span
                                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${SAFETY_RATING_TONE[snap.safety?.rating] || A_N_I_TONE.u}`}
                                    data-testid="fmcsa-snap-safety-rating"
                                >
                                    {snap.safety?.rating ? SAFETY_RATING_LABEL[snap.safety.rating] : "No rating on file"}
                                </span>
                                {snap.safety?.rating_date && <span className="text-[11px] text-slate-500">Rated {fmtDate(snap.safety.rating_date)}</span>}
                                {snap.safety?.iss_score != null && <span className="text-[11px] text-slate-500">ISS {snap.safety.iss_score}</span>}
                            </div>
                            <div className="text-[11px] text-slate-500">
                                Review: {snap.safety?.review_type || "—"}{" "}
                                {snap.safety?.review_date && `(${fmtDate(snap.safety.review_date)})`}
                            </div>
                            {snap.safety?.basics?.length > 0 && (
                                <div className="mt-3" data-testid="fmcsa-snap-basics-table">
                                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">CSA BASICs</p>
                                    <ul className="text-xs space-y-1">
                                        {snap.safety.basics.map((b, idx) => (
                                            <li key={idx} className="flex justify-between"><span>{b.name || b.basicId || "?"}</span><span className="text-slate-500">{b.measure ?? "—"} ({b.percentile ?? "—"}%)</span></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Section>

                        <Section title="Crashes (24 mo)" icon={ShieldAlert} testId="fmcsa-snapshot-section-crashes">
                            <div className="grid grid-cols-4 gap-2">
                                {[
                                    { k: "total", label: "Total" },
                                    { k: "fatal", label: "Fatal" },
                                    { k: "injury", label: "Injury" },
                                    { k: "towaway", label: "Tow-away" },
                                ].map((c) => (
                                    <div key={c.k} className="rounded-md bg-slate-50 border border-slate-100 p-2 text-center">
                                        <p className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</p>
                                        <p className="text-lg font-semibold text-slate-900 tabular-nums" data-testid={`fmcsa-snap-crash-${c.k}`}>{snap.crashes?.[c.k] ?? 0}</p>
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section title="OOS Rates (24 mo)" icon={Activity} testId="fmcsa-snapshot-section-oos">
                            <OosRateRow label="Driver" group={snap.inspections?.driver} testIdBase="fmcsa-snap-oos-driver" />
                            <OosRateRow label="Vehicle" group={snap.inspections?.vehicle} testIdBase="fmcsa-snap-oos-vehicle" />
                            <OosRateRow label="HazMat" group={snap.inspections?.hazmat} testIdBase="fmcsa-snap-oos-hazmat" />
                            <p className="text-[11px] text-slate-400 mt-2">National avg period: {snap.inspections?.oos_national_avg_year || "—"}</p>
                        </Section>

                        <Section title="Insurance on file" icon={FileText} testId="fmcsa-snapshot-section-insurance">
                            <InsuranceRow label="BIPD" group={snap.insurance?.bipd} testIdBase="fmcsa-snap-ins-bipd" />
                            <InsuranceRow label="Cargo" group={snap.insurance?.cargo} testIdBase="fmcsa-snap-ins-cargo" />
                            <InsuranceRow label="Bond" group={snap.insurance?.bond} testIdBase="fmcsa-snap-ins-bond" />
                        </Section>

                        <Section title="Operations" icon={Truck} testId="fmcsa-snapshot-section-operations" defaultOpen={false}>
                            <dl className="divide-y divide-slate-50">
                                <Row label="Carrier operation">{snap.carrier_operation || "—"}</Row>
                                <Row label="Op classifications">{(snap.operation_classifications || []).join(", ") || "—"}</Row>
                                <Row label="Cargo carried">{(snap.cargo_carried || []).join(", ") || "—"}</Row>
                                <Row label="Total drivers">{fmtNum(snap.total_drivers)}</Row>
                                <Row label="Total power units">{fmtNum(snap.total_power_units)}</Row>
                                <Row label="Status code">{snap.status_code || "—"}</Row>
                            </dl>
                        </Section>
                    </div>
                )}
                </CardContent>
            )}
        </Card>
    );
}

export default FmcsaSnapshotPanel;
